import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injector, SecurityContext } from '@angular/core';
import { Injectable } from '@angular/core';
import { ToastSandbox } from '../sandboxes/toast.sandbox';
import { environment } from '../../../environments/environment';
import { IonicSafeString } from '@ionic/angular';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  // Error handling is important and needs to be loaded first.
  // Because of this we should manually inject the services with Injector.
  constructor(
    private injector: Injector,
    private toastSandbox: ToastSandbox,
    private readonly translocoSandbox: TranslationSandbox,
    private sanitizer: DomSanitizer
  ) {}

  handleError(error: Error | HttpErrorResponse | UncaughtPromiseError) {
    const promiseError = error as UncaughtPromiseError;
    if (promiseError?.rejection) {
      error = promiseError.rejection;
    }

    const errorMessage: ErrorMessage =
      error instanceof HttpErrorResponse ? this.handleServerError(error) : this.handleClientError(error);

    if (!environment.production) {
      console.error(error);
    }

    if (!errorMessage.shouldDisplay) {
      return;
    }

    this.toastSandbox.showErrorToast({
      header: errorMessage.title,
      message: errorMessage.message ?? this.translocoSandbox.translate('error.whoops'),
    });
  }

  handleServerError(error: HttpErrorResponse): ErrorMessage {
    if (!navigator.onLine) {
      return {
        message: 'No Internet Connection',
        shouldDisplay: true,
      };
    }

    const errorId = error?.error?.id;
    const title =  error?.error?.title;
    const message = error?.error?.message ?? this.translocoSandbox.translate('error.whoops');
    const messageLimitedTo500Chars = message.substring(0, 500);

    if (errorId) {
      return {
        title: title,
        message: new IonicSafeString(
          this.sanitizer.sanitize(
            SecurityContext.HTML,
            `
            ${messageLimitedTo500Chars}
            <br/>
            <br/>
            Admin code: ${errorId}`
          )
        ),
        shouldDisplay: true,
      };
    }

    return {
      title: title,
      message: messageLimitedTo500Chars,
      shouldDisplay: true,
    };
  }

  handleClientError(error: Error): ErrorMessage {
    if (!navigator.onLine) {
      return {
        message: 'No Internet Connection',
        shouldDisplay: true,
      };
    }

    return {
      message: error.message,
      shouldDisplay: false,
    };
  }
}

interface ErrorMessage {
  title?: string;
  message: string | IonicSafeString;
  shouldDisplay: boolean;
}
